import PropTypes from 'prop-types';
import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import styled, { useTheme } from 'styled-components';
import {
  HelpTip,
  Icon,
  inkA,
  orange,
  PlainButton,
  tokens,
} from 'components/v3';
import { CATEGORIES, PALETTE_ORDER, paletteEntry } from '../shared/fieldTypes';
import {
  DROP_GHOST_CLASS,
  PALETTE_GROUP,
  paletteClone,
  paletteItems,
  setDragData,
} from './state/sortableBridge';

// The field types, grouped Basic / Advanced / Layout, each group with its
// help tip (plan §5.4). Tap an item to add it. Shared by the desktop
// palette (where `draggable` also makes each group a react-sortablejs
// clone source) and the phone Add Field sheet.

const Item = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: ${(p) => (p.$tall ? 44 : 38)}px;
  padding: 9px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.2;
  text-align: left;
  cursor: ${(p) => (p.$draggable ? 'grab' : 'pointer')};
  user-select: none;
  transition: border-color 150ms ease, background 150ms ease,
    transform 150ms ease;

  &:hover {
    border-color: ${orange(1)};
    background: ${orange(0.08)};
    transform: translateY(-1px);
  }

  &.sortable-chosen {
    cursor: grabbing;
  }
`;

const GRID = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 };

// Palette groups never take drops, so their setList has nothing to do.
const ignoreList = () => undefined;

export const matchTypes = (types, query) => {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return types;
  return types.filter((type) =>
    paletteEntry(type).label.toLowerCase().includes(q)
  );
};

const PaletteList = ({ query, onAdd, draggable, tall }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const groups = CATEGORIES.map((cat) => ({
    ...cat,
    types: matchTypes(PALETTE_ORDER[cat.key], query),
  })).filter((g) => g.types.length);

  if (!groups.length) {
    return (
      <div
        style={{
          padding: '24px 8px',
          textAlign: 'center',
          fontSize: 13,
          color: t.faint,
        }}
      >
        {`No fields match “${String(query).trim()}”`}
      </div>
    );
  }

  const item = (type) => (
    <Item
      key={type}
      data-help="palette-item"
      title="Tap to add, or drag onto the form"
      onClick={() => onAdd(type)}
      $tall={tall}
      $draggable={draggable}
      $border={t.border}
      $bg={inkA(theme, 0.04)}
      $color={t.sub}
    >
      <Icon name={paletteEntry(type).icon} size={16} color={orange(1)} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {paletteEntry(type).label}
      </span>
    </Item>
  );

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {groups.map((group) => (
        <div
          key={group.key}
          role="group"
          aria-labelledby={`fb-palette-${group.key}`}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              margin: '0 0 10px 2px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: t.primary,
            }}
          >
            <span id={`fb-palette-${group.key}`}>{group.label}</span>
            <HelpTip content={group.help} size={14} width={260} />
          </div>
          {draggable ? (
            <ReactSortable
              list={paletteItems(group.types)}
              setList={ignoreList}
              group={PALETTE_GROUP}
              sort={false}
              clone={paletteClone}
              setData={setDragData}
              ghostClass={DROP_GHOST_CLASS}
              delayOnTouchOnly
              delay={120}
              style={GRID}
            >
              {group.types.map(item)}
            </ReactSortable>
          ) : (
            <div style={GRID}>{group.types.map(item)}</div>
          )}
        </div>
      ))}
    </div>
  );
};

PaletteList.propTypes = {
  query: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  draggable: PropTypes.bool,
  tall: PropTypes.bool,
};

PaletteList.defaultProps = { query: '', draggable: false, tall: false };

export default PaletteList;
